'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import {
  FaGlobeEurope,
  FaRocket,
  FaBox,
  FaRepeat,
  GiCargoCrane,
  HiBeaker,
  MdOutlineSupport,
  SiLetsencrypt
} from "@/components/ReactIcons";

// Modern, animated heading component for feature sections
export function FeatureHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12 text-text-main">
      <span className="bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to bg-clip-text text-transparent">
        {children}
      </span>
    </h2>
  );
}

// Improved advantages section with custom colors
export function AdvantagesSection({
  advantageTexts,
}: {
  advantageTexts?: Record<string, { title: string; description: string }>;
}) {
  const tAdvantages = useTranslations('advantages');
  const advantagesRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Set mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Features data with keys matching translation keys
  const features = [
    { key: 'unmatchedQuality', icon: <HiBeaker className="w-full h-full" /> },
    { key: 'globalShipping', icon: <FaGlobeEurope className="w-full h-full" /> },
    { key: 'securePayments', icon: <SiLetsencrypt className="w-full h-full" /> },
    { key: 'customSolutions', icon: <GiCargoCrane className="w-full h-full" /> },
    { key: 'bulkPricing', icon: <FaBox className="w-[90%] h-[90%]" /> },
    { key: 'innovationEdge', icon: <FaRocket className="w-[90%] h-[90%]" /> },
    { key: 'reliableSupply', icon: <FaRepeat className="w-[90%] h-[90%]" /> },
    { key: 'expertSupport', icon: <MdOutlineSupport className="w-[110%] h-[110%]" /> }
  ];

  return (
    <div ref={advantagesRef} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full sm:h-full overflow-x-auto sm:overflow-hidden flex items-center">
      <div className="relative w-full">
        <div className={isMobile ? "flex flex-nowrap items-stretch" : "grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"}>
          {features.map((feature, idx) => (
            <div 
              key={`feature-${idx}`}
              className="p-6 rounded-xl backdrop-blur-sm transition-all duration-300 flex flex-col flex-1 h-full min-h-0 min-w-[200px] sm:min-w-0
                bg-bg-secondary border-0 sm:border hover:border-primary/30 group"
            >
              <div className="w-12 h-12 mb-5 rounded-full bg-bg-main flex items-center justify-center text-primary p-3 group-hover:text-accent transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="text-lg font-medium mb-2 text-text-main group-hover:text-primary transition-colors duration-300">
                {advantageTexts?.[feature.key]?.title || tAdvantages(`${feature.key}.title`)}
              </h3>
              <p className="text-text-secondary">
                {advantageTexts?.[feature.key]?.description || tAdvantages(`${feature.key}.description`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Modern hero section component with gradient background
export function HeroSection({ title, subtitle, ctaButtons }: { title: string; subtitle: string; ctaButtons: React.ReactNode }) {
  return (
    <div className="relative h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-bg-main to-bg-secondary" />
      
      {/* Animated gradient spheres for visual interest */}
      <div className="absolute top-0 right-0 w-[40vw] h-[40vw] rounded-full bg-primary/10 blur-[120px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[40vw] h-[40vw] rounded-full bg-accent/10 blur-[120px] translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative z-10 max-w-7xl mx-auto text-center px-4">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-text-main">
          <span className="bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to bg-clip-text text-transparent">
            {title}
          </span>
        </h1>
        <p className="text-lg sm:text-xl lg:text-2xl max-w-3xl mx-auto text-text-secondary mb-12">
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
          {ctaButtons}
        </div>
      </div>
    </div>
  );
}

// Modern shipping section with gradient background
export function ShippingSection({ title, description, features, ctaButton }: { 
  title: string; 
  description: string; 
  features: { text: string }[];
  ctaButton: React.ReactNode;
}) {
  return (
    <div className="py-16 md:py-24 w-full relative overflow-hidden">
      {/* Decorative gradient background elements */}
      <div className="absolute right-0 top-0 w-[30rem] h-[30rem] rounded-full bg-primary/5 blur-[100px] translate-x-1/2 -translate-y-1/2" />
      <div className="absolute left-0 bottom-0 w-[30rem] h-[30rem] rounded-full bg-accent/5 blur-[100px] -translate-x-1/2 translate-y-1/2" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-bg-secondary border border-border p-8 md:p-12">
          <div className="md:flex items-center gap-12">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-text-main">
                {title}
              </h2>
              <p className="text-lg text-text-secondary mb-8">
                {description}
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="text-text-main">{feature.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                {ctaButton}
              </div>
            </div>
            <div className="md:w-1/2 relative h-60 md:h-96 rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modern about section with cards
export function AboutSection({ title, subtitle, stats, story, values, ctaButtons }: {
  title: string;
  subtitle: string;
  stats: { value: string; label: string }[];
  story: { title: string; paragraphs: string[] };
  values: { title: string; description: string }[];
  ctaButtons: React.ReactNode;
}) {
  return (
    <div className="py-16 md:py-24 w-full relative overflow-hidden">
      {/* Decorative gradient background elements */}
      <div className="absolute left-0 top-1/4 w-[30rem] h-[30rem] rounded-full bg-primary/5 blur-[100px] -translate-x-1/2" />
      <div className="absolute right-0 bottom-1/4 w-[25rem] h-[25rem] rounded-full bg-accent/5 blur-[100px] translate-x-1/2" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-bg-secondary border border-border p-8 md:p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to bg-clip-text text-transparent">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-center text-text-secondary mb-12 max-w-3xl mx-auto">
            {subtitle}
          </p>
          
          {/* Stats section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mb-16">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center p-6 rounded-xl bg-bg-main border border-border">
                <span className="block text-3xl font-bold text-primary mb-2">{stat.value}</span>
                <span className="text-sm text-text-secondary">{stat.label}</span>
              </div>
            ))}
          </div>
          
          {/* Story section */}
          <div className="mb-16">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center text-text-main">
              {story.title}
            </h3>
            <div className="max-w-3xl mx-auto space-y-4 text-text-secondary text-center">
              {story.paragraphs.map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </div>
          
          {/* Values section */}
          <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center text-text-main">
            {values[0].title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {values.slice(1).map((value, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-bg-main border border-border hover:border-primary/30 transition-colors duration-300">
                <h4 className="text-xl font-semibold mb-3 text-primary">
                  {value.title}
                </h4>
                <p className="text-text-secondary">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
          
          {/* CTA section */}
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-text-main">
              Ready to get started?
            </h3>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Join our growing network of satisfied business clients today.
            </p>
            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
              {ctaButtons}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modern product showcase section
export function ProductShowcase() {
  return (
    <div className="relative py-16 md:py-24 overflow-hidden">
      {/* Background gradient elements */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 z-0 pointer-events-none">
        <div className="w-[40rem] h-96 relative">
          <div className="absolute inset-0 rounded-full bg-primary/10 blur-[100px] scale-x-150"></div>
          <div className="absolute inset-0 rounded-full bg-primary/20 blur-[80px] scale-x-125"></div>
        </div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FeatureHeading>Featured Products</FeatureHeading>
        {/* Product grid will be rendered here by the parent component */}
      </div>
    </div>
  );
}

// Slimmed down exports, removing old unnecessary components
export function InteractiveImage(props: React.ComponentProps<typeof Image>) {
  return (
    <Image
      {...props}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      alt={props.alt}
    />
  );
}